import React, { useEffect } from 'react';
import useToolStore, { TOOL_TYPES } from '../../../store/useToolStore';
import { generateCode } from '../../../utils/codeGenerators';

const ButtonBuilder = () => {
  const config = useToolStore((state) => state.toolConfigs[TOOL_TYPES.BUTTON]);
  const updateConfig = useToolStore((state) => state.updateToolConfig);
  const setGeneratedCode = useToolStore((state) => state.setGeneratedCode);

  useEffect(() => {
    const code = generateCode(TOOL_TYPES.BUTTON, config);
    setGeneratedCode(code);
  }, [config, setGeneratedCode]);

  const set = (patch) => updateConfig(TOOL_TYPES.BUTTON, patch);

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Button Builder</h2>

      <div className="space-y-4">
        <label className="block">
          <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Title</div>
          <input
            type="text"
            value={config.title}
            onChange={(e) => set({ title: e.target.value })}
            className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Text color</div>
            <input
              type="color"
              value={config.textColor}
              onChange={(e) => set({ textColor: e.target.value })}
              className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            />
          </label>
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Background</div>
            <input
              type="color"
              value={config.backgroundColor}
              onChange={(e) => set({ backgroundColor: e.target.value })}
              className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            />
          </label>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Padding V</div>
            <input
              type="number"
              value={config.paddingV}
              onChange={(e) => set({ paddingV: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </label>
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Padding H</div>
            <input
              type="number"
              value={config.paddingH}
              onChange={(e) => set({ paddingH: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </label>
          <label className="block">
            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Corner radius</div>
            <input
              type="number"
              value={config.cornerRadius}
              onChange={(e) => set({ cornerRadius: Number(e.target.value) })}
              className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </label>
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.borderEnabled}
              onChange={(e) => set({ borderEnabled: e.target.checked })}
            />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Border</span>
          </label>
          {config.borderEnabled && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Width</div>
                <input
                  type="number"
                  value={config.borderWidth}
                  onChange={(e) => set({ borderWidth: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Color</div>
                <input
                  type="color"
                  value={config.borderColor}
                  onChange={(e) => set({ borderColor: e.target.value })}
                  className="h-10 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                />
              </label>
            </div>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={config.shadowEnabled}
              onChange={(e) => set({ shadowEnabled: e.target.checked })}
            />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Shadow</span>
          </label>
          {config.shadowEnabled && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Opacity</div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={config.shadowOpacity}
                  onChange={(e) => set({ shadowOpacity: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Radius</div>
                <input
                  type="number"
                  value={config.shadowRadius}
                  onChange={(e) => set({ shadowRadius: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">X</div>
                <input
                  type="number"
                  value={config.shadowX}
                  onChange={(e) => set({ shadowX: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </label>
              <label className="block">
                <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Y</div>
                <input
                  type="number"
                  value={config.shadowY}
                  onChange={(e) => set({ shadowY: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const ButtonPreviewComponent = () => {
  const config = useToolStore((state) => state.toolConfigs[TOOL_TYPES.BUTTON]);

  const style = {
    backgroundColor: config.backgroundColor,
    color: config.textColor,
    padding: `${config.paddingV}px ${config.paddingH}px`,
    borderRadius: `${config.cornerRadius}px`,
    border: config.borderEnabled ? `${config.borderWidth}px solid ${config.borderColor}` : 'none',
    boxShadow: config.shadowEnabled
      ? `${config.shadowX}px ${config.shadowY}px ${config.shadowRadius}px rgba(0,0,0,${config.shadowOpacity})`
      : 'none',
  };

  return (
    <div className="h-full w-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
      <button style={style} className="font-semibold select-none">
        {config.title || 'Button'}
      </button>
    </div>
  );
};

export default ButtonBuilder;

